/**
 * Vorlage „Frittieren“.
 *
 * Ein Korb mit hellen, rohen Stücken senkt sich in das heiße Fett. Es
 * sprudelt kräftig. Danach wird der Korb goldbraun wieder herausgehoben und
 * tropft ab. Die Plakette zeigt die Temperatur des Fetts, die Uhr die Zeit.
 */
import { ease, lerp, random, segment } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { path, roundedRectPath } from '../paths.ts';
import { circle, compact, faded, group, line, rect, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { bubbles, falling, sizzle } from '../parts/effects.ts';
import { badge, counter, doubleArrow, durationLabel, timerBadge } from '../parts/stage.ts';
import { GRATE_Y, stove, stoveKnob } from '../parts/stove.ts';
import { contents, pot } from '../parts/vessels.ts';
import { minutesFromText } from '../inference.ts';
import { choice, defineTemplate, numberValue, textValue } from '../template.ts';

const POT = { cx: 100, width: 124, height: 52 } as const;
const RIM_Y = GRATE_Y - POT.height;
const LEVEL = 0.36;
/** Korb: Breite, Höhe und Lage der Unterkante oben bzw. im Fett. */
const BASKET = { width: 72, height: 24, upY: RIM_Y - 4, downY: RIM_Y + 40 } as const;

/** Mitte der Uhr; rechts oben ist der Griff des Korbs. */
const TIMER_X = 120;

/** Was frittiert wird. */
const ITEMS = { fries: 'Pommes', balls: 'Bällchen' } as const;

/** Zeitplan. */
const TIME = {
  appear: [0, 0.05],
  lower: [0.06, 0.18],
  fry: [0.18, 0.68],
  lift: [0.68, 0.78],
  drip: [0.78, 0.92],
  vanish: [0.92, 1],
} as const;

/** Inhalt des Korbs: Stäbchen oder Bällchen, roh hell und fertig gebräunt. */
function food(item: keyof typeof ITEMS, left: number, bottom: number, brown: number): SceneNode[] {
  const rng = random(item === 'fries' ? 13 : 29);
  const nodes: SceneNode[] = [];
  const count = item === 'fries' ? 7 : 6;
  for (let index = 0; index < count; index += 1) {
    const x = left + 8 + (index / (count - 1)) * (BASKET.width - 16);
    const jitter = rng();
    if (item === 'fries') {
      const angle = (jitter - 0.5) * 36;
      const piece = (tone: 'paper' | 'shade'): SceneNode =>
        group([rect(x - 3, bottom - 20, 6, 18, 2, { fill: tone, stroke: 'ink', strokeWidth: STROKE.fine })], {
          transform: { rotate: angle, ox: x, oy: bottom - 4 },
        });
      nodes.push(piece('paper'));
      if (brown > 0.01) {
        nodes.push(group([piece('shade')], { opacity: brown }));
      }
    } else {
      const cy = bottom - 6 - (index % 2) * 7 - jitter * 2;
      nodes.push(circle(x, cy, 6, { fill: 'paper', stroke: 'ink', strokeWidth: STROKE.fine }));
      if (brown > 0.01) {
        nodes.push(
          circle(x, cy, 6, { fill: 'shade', stroke: 'ink', strokeWidth: STROKE.fine, opacity: brown }),
        );
      }
    }
  }
  return nodes;
}

/** Drahtkorb mit Griff nach rechts oben; Unterkante bei bottom. */
function basket(left: number, bottom: number, inside: readonly SceneNode[]): SceneNode {
  const top = bottom - BASKET.height;
  const outline = roundedRectPath(left, top, BASKET.width, BASKET.height, 4);
  const mesh: SceneNode[] = [];
  // Nur ein grobes Gitter, damit der Inhalt erkennbar bleibt.
  for (let x = left + 18; x < left + BASKET.width; x += 18) {
    mesh.push(line(x, top, x, bottom, styles.fine('deep')));
  }
  mesh.push(
    line(left, top + BASKET.height / 2, left + BASKET.width, top + BASKET.height / 2, styles.fine('deep')),
  );
  const right = left + BASKET.width;
  return group([
    group([...inside, ...mesh], { clip: outline }),
    shape(outline, styles.outline()),
    shape(
      path()
        .move(right - 2, top + 2)
        .line(right + 22, top - 6)
        .toString(),
      styles.outline(),
    ),
    shape(roundedRectPath(right + 18, top - 11, 20, 8, 4), styles.solid('ink')),
  ]);
}

/** Vorlage „Frittieren“. */
export const stoveDeepFryTemplate = defineTemplate({
  id: 'stove.deep-fry',
  group: 'stove',
  title: 'Frittieren',
  description:
    'Der Korb mit den Stücken wird in heißes Fett gesenkt, sprudelnd goldbraun gebacken, herausgehoben und abgetropft.',
  durationSeconds: 6,
  keywords: [
    'frittieren',
    'frittiert',
    'fritteuse',
    'ausbacken',
    'im fett',
    'in heißem öl',
    'in fett',
    'pommes',
  ],
  stillTime: 0.84,
  parameters: [
    {
      kind: 'number',
      name: 'temperature',
      label: 'Temperatur',
      min: 140,
      max: 190,
      step: 5,
      unit: '°C',
      defaultValue: 170,
    },
    {
      kind: 'number',
      name: 'minutes',
      label: 'Dauer',
      min: 1,
      max: 20,
      step: 1,
      unit: 'Min',
      defaultValue: 4,
    },
    choice('item', 'Inhalt', ITEMS, 'fries'),
  ],
  inferValues(text) {
    const lower = text.toLowerCase();
    const values: Record<string, string | number | undefined> = { minutes: minutesFromText(text) };
    const match = /(\d{3})\s*(?:°|grad)/.exec(lower);
    if (match?.[1] !== undefined) {
      values.temperature = Number(match[1]);
    }
    if (/bällchen|krapfen|kugeln|falafel|kroketten/.test(lower)) values.item = 'balls';
    else if (/pommes|kartoffel|stäbchen/.test(lower)) values.item = 'fries';
    return values;
  },
  scene(values, t) {
    const temperature = numberValue(values, 'temperature', 170);
    const minutes = numberValue(values, 'minutes', 4);
    const item = textValue(values, 'item', 'fries') as keyof typeof ITEMS;
    const vessel = pot(POT.cx, RIM_Y, POT.width, POT.height);
    const burner = stove(POT.cx, 0.7, t);
    const surfaceY = RIM_Y + vessel.depth * LEVEL;
    const rx = vessel.widthAt(LEVEL);
    const ry = vessel.ry * (rx / vessel.rx);

    const shown = ease.outCubic(segment(t, ...TIME.appear)) * (1 - ease.inCubic(segment(t, ...TIME.vanish)));
    const down = ease.inOutCubic(segment(t, ...TIME.lower)) - ease.inOutCubic(segment(t, ...TIME.lift));
    const brown = ease.inOutSine(segment(t, ...TIME.fry));
    const dripping = t > TIME.drip[0] && t < TIME.drip[1];
    const bottom = lerp(BASKET.upY, BASKET.downY, down);
    const left = POT.cx - BASKET.width / 2;

    const oil = contents(vessel, LEVEL, 'tint', [
      ...bubbles(POT.cx - rx, surfaceY - ry, rx * 2, ry * 1.8, 0.3 + 0.7 * down, t, 4),
    ]);
    return compact([
      ...counter(),
      ...burner.back,
      ...vessel.back,
      oil,
      faded(shown, [basket(left, bottom, food(item, left, bottom, brown))]),
      ...vessel.front,
      ...burner.front,
      ...sizzle(POT.cx, RIM_Y - 2, rx * 0.8, down, t, 6),
      ...(dripping
        ? falling(POT.cx, BASKET.upY + 2, surfaceY, BASKET.width * 0.8, 6, t, {
            loops: 4,
            size: 1.6,
            tone: 'deep',
          })
        : []),
      stoveKnob(206, 0.7),
      badge(42, 14, `${Math.round(temperature)} °C`, 'accent'),
      timerBadge(TIMER_X, 13, durationLabel(minutes), t, 'center'),
    ]);
  },
  hints() {
    return [doubleArrow({ x: 16, y: RIM_Y - 22 }, { x: 16, y: RIM_Y + 26 })];
  },
});
